import { useCallback, useEffect, useMemo, useState } from 'react'
import api from '../../../helpers/apiRequester'
import {
    Banner,
    Button,
    Card,
    Column,
    DataTable,
    Input,
    PillTone,
    StackCell,
    StatCard,
    StatGrid,
    StatusPill,
    Toolbar,
} from '../../../ui'
import styles from '../commsShared/admin.module.scss'

/**
 * Cancellation readiness
 *
 * Read-only probe of every introducer in the cancellation registry
 * (`GET /actions/cancel/capabilities` on the API, which runs the same
 * `cancellationCapabilities()` the action server exposes). Nothing here writes
 * to an introducer database: each row is "can we reach the DB, does it have the
 * columns the adapter writes, is there an audit table, can we read one case".
 */

type ReadinessStatus =
    | 'ready'
    | 'ready_no_audit'
    | 'disabled'
    | 'adapter_error'
    | 'unreachable'
    | 'schema_error'
    | 'schema_missing'
    | 'sample_failed'

interface SchemaCheck {
    ok: boolean
    missing: { table: string; column: string | null }[]
    present: Record<string, boolean>
    optionalMissing?: { table: string; column: string | null }[]
}

interface SampleCheck {
    caseRef: string | null
    found: boolean
    hasState: boolean
    cancelled: boolean | null
}

interface IntroducerReadiness {
    introducer: string
    label: string
    aliases: string[]
    schemaFamily: 'leads' | 'matters' | 'oa'
    credentialKey: string | null
    letterType: string | null
    pushPe: boolean
    enabled: boolean
    disabledReason: string | null
    ready: boolean
    status: ReadinessStatus
    error: string | null
    warnings: string[]
    checks: {
        reachable: boolean | null
        schema: SchemaCheck | null
        auditTable: boolean | null
        sample: SampleCheck | null
    }
}

interface CapabilitiesResponse {
    success: boolean
    checkedAt: string
    summary: { total: number; ready: number; disabled: number; failing: number }
    introducers: IntroducerReadiness[]
    message?: string
}

const STATUS_META: Record<ReadinessStatus, { label: string; tone: PillTone; help: string }> = {
    ready: { label: 'Ready', tone: 'good', help: 'Reachable, schema complete, audit table present.' },
    ready_no_audit: {
        label: 'Ready (no audit)',
        tone: 'warn',
        help: 'Cancellations work but nothing is written to case_cancellation_log; add the table to keep an audit trail.',
    },
    disabled: { label: 'Disabled', tone: 'neutral', help: 'Deliberately excluded in the registry.' },
    schema_missing: {
        label: 'Schema missing',
        tone: 'bad',
        help: 'Required tables are absent — usually an empty database.',
    },
    schema_error: { label: 'Schema check failed', tone: 'bad', help: 'Could not read the table structure.' },
    unreachable: { label: 'Unreachable', tone: 'bad', help: 'Could not connect with the configured credentials.' },
    adapter_error: {
        label: 'Adapter error',
        tone: 'bad',
        help: 'No adapter for this schema family or it failed to construct.',
    },
    sample_failed: { label: 'Sample failed', tone: 'bad', help: 'Could not read a sample case.' },
}

const FAMILY_LABEL: Record<IntroducerReadiness['schemaFamily'], string> = {
    leads: 'Leads (leads + clients + lead_states)',
    matters: 'Matters (Zapped Cases)',
    oa: 'OA (oaGenieSync)',
}

const fmtDate = (raw: string | null | undefined) =>
    raw
        ? new Date(raw).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—'

type Filter = 'all' | 'ready' | 'attention' | 'disabled'

const isFailing = (row: IntroducerReadiness) =>
    row.status !== 'ready' && row.status !== 'ready_no_audit' && row.status !== 'disabled'

const matchesFilter = (row: IntroducerReadiness, filter: Filter) => {
    if (filter === 'all') return true
    if (filter === 'ready') return row.status === 'ready' || row.status === 'ready_no_audit'
    if (filter === 'disabled') return row.status === 'disabled'
    return isFailing(row)
}

export const CancellationReadinessView = () => {
    const [data, setData] = useState<CapabilitiesResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [filter, setFilter] = useState<Filter>('all')
    const [search, setSearch] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api.get('/actions/cancel/capabilities')
            if (res.data?.success) {
                setData(res.data)
            } else {
                setError(res.data?.message || 'Could not run the readiness probe.')
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Could not run the readiness probe.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const rows = useMemo(() => {
        const list = (data?.introducers ?? []).map((r) => ({
            ...r,
            aliases: Array.isArray(r.aliases) ? r.aliases : [],
            warnings: Array.isArray(r.warnings) ? r.warnings : [],
            checks: r.checks ?? { reachable: null, schema: null, auditTable: null, sample: null },
        }))
        const q = search.trim().toLowerCase()
        return list
            .filter((r) => matchesFilter(r, filter))
            .filter(
                (r) =>
                    !q ||
                    r.introducer.toLowerCase().includes(q) ||
                    r.label.toLowerCase().includes(q) ||
                    r.aliases.some((a) => a.toLowerCase().includes(q)) ||
                    (r.credentialKey ?? '').toLowerCase().includes(q),
            )
            .sort((a, b) => {
                const rank = (r: IntroducerReadiness) =>
                    r.status === 'ready' ? 2 : r.status === 'disabled' ? 3 : r.status === 'ready_no_audit' ? 1 : 0
                return rank(a) - rank(b) || a.label.localeCompare(b.label)
            })
    }, [data, filter, search])

    const attention = useMemo(() => (data?.introducers ?? []).filter(isFailing).length, [data])
    const noAudit = useMemo(() => (data?.introducers ?? []).filter((r) => r.status === 'ready_no_audit').length, [data])

    const columns: Column<IntroducerReadiness>[] = [
        {
            key: 'introducer',
            header: 'Introducer',
            render: (r) => (
                <StackCell
                    primary={r.label}
                    secondary={
                        r.aliases
                            .filter((a) => a !== r.label && a !== r.introducer)
                            .slice(0, 4)
                            .join(' · ') || r.introducer
                    }
                />
            ),
        },
        {
            key: 'status',
            header: 'Status',
            nowrap: true,
            render: (r) => (
                <StatusPill tone={STATUS_META[r.status]?.tone ?? 'neutral'} dot title={STATUS_META[r.status]?.help}>
                    {STATUS_META[r.status]?.label ?? r.status}
                </StatusPill>
            ),
        },
        {
            key: 'family',
            header: 'Schema / database',
            render: (r) => (
                <StackCell
                    primary={FAMILY_LABEL[r.schemaFamily] ?? r.schemaFamily}
                    secondary={r.credentialKey ?? 'no credentials'}
                />
            ),
        },
        {
            key: 'letter',
            header: 'Letter',
            render: (r) => (
                <StackCell
                    primary={r.letterType ?? '—'}
                    secondary={r.pushPe ? 'Also pushed to Proclaim' : 'Database + letter only'}
                />
            ),
        },
        {
            key: 'checks',
            header: 'Checks',
            nowrap: true,
            render: (r) => {
                if (r.status === 'disabled')
                    return <span className={styles.sub}>{r.disabledReason ?? 'Disabled in the registry'}</span>
                const c = r.checks
                return (
                    <div className={styles.actions}>
                        <StatusPill size="sm" tone={c.reachable ? 'good' : c.reachable === false ? 'bad' : 'neutral'}>
                            DB
                        </StatusPill>
                        <StatusPill size="sm" tone={c.schema?.ok ? 'good' : c.schema ? 'bad' : 'neutral'}>
                            Schema
                        </StatusPill>
                        <StatusPill
                            size="sm"
                            tone={c.auditTable ? 'good' : c.auditTable === false ? 'warn' : 'neutral'}
                        >
                            Audit log
                        </StatusPill>
                        <StatusPill
                            size="sm"
                            tone={c.sample ? (c.sample.found ? 'good' : 'warn') : 'neutral'}
                            title={c.sample?.caseRef ? `Sample case ${c.sample.caseRef}` : undefined}
                        >
                            Sample
                        </StatusPill>
                    </div>
                )
            },
        },
        {
            key: 'detail',
            header: 'Detail',
            render: (r) => {
                const bits = Array.from(new Set([r.error, ...r.warnings].filter((b): b is string => Boolean(b))))
                return <span className={styles.sub}>{bits.length ? bits.join(' · ') : '—'}</span>
            },
        },
    ]

    const filters: { key: Filter; label: string; count: number }[] = [
        { key: 'all', label: 'All', count: data?.summary.total ?? 0 },
        { key: 'ready', label: 'Ready', count: data?.summary.ready ?? 0 },
        { key: 'attention', label: 'Needs attention', count: attention },
        { key: 'disabled', label: 'Disabled', count: data?.summary.disabled ?? 0 },
    ]

    return (
        <div className={styles.stack}>
            <StatGrid>
                <StatCard
                    label="Introducers in registry"
                    value={data?.summary.total ?? '—'}
                    sub="Every entry with a cancellation adapter"
                />
                <StatCard
                    label="Ready"
                    value={data?.summary.ready ?? '—'}
                    tone={
                        data && data.summary.ready === data.summary.total - data.summary.disabled ? 'good' : 'default'
                    }
                    sub={noAudit ? `${noAudit} without an audit table` : 'Reachable with a complete schema'}
                />
                <StatCard
                    label="Needs attention"
                    value={data ? attention : '—'}
                    tone={attention ? 'bad' : 'good'}
                    sub="Unreachable, schema missing or probe failed"
                />
                <StatCard
                    label="Disabled"
                    value={data?.summary.disabled ?? '—'}
                    tone="muted"
                    sub="Excluded on purpose in the registry"
                />
            </StatGrid>

            {error && (
                <Banner
                    tone="bad"
                    title="Readiness probe failed"
                    actions={
                        <Button size="sm" variant="neutral" onClick={load}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Banner>
            )}

            {!error && noAudit > 0 && (
                <Banner
                    tone="warn"
                    title={`${noAudit} introducer${noAudit === 1 ? '' : 's'} cancel without an audit trail`}
                >
                    These databases have no <code>case_cancellation_log</code> table, so cancellations succeed but are
                    not recorded. Adding the table is a plain <code>CREATE TABLE</code> (no data changes) and the probe
                    will pick it up on the next refresh.
                </Banner>
            )}

            <Card
                title="Introducers"
                subtitle="Read-only checks. Nothing on this page writes to an introducer database."
                padding="none"
                actions={
                    <span className={styles.actions}>
                        <span className={styles.sub}>Checked {fmtDate(data?.checkedAt)}</span>
                        <Button size="sm" variant="neutral" onClick={load} loading={loading}>
                            Refresh
                        </Button>
                    </span>
                }
            >
                <Toolbar
                    end={
                        <Input
                            placeholder="Search introducer, alias or credential key…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search introducers"
                        />
                    }
                >
                    <div className={styles.actions} role="tablist" aria-label="Filter introducers">
                        {filters.map((f) => (
                            <Button
                                key={f.key}
                                size="sm"
                                variant={filter === f.key ? 'primary' : 'ghost'}
                                onClick={() => setFilter(f.key)}
                                role="tab"
                                aria-selected={filter === f.key}
                            >
                                {f.label} ({f.count})
                            </Button>
                        ))}
                    </div>
                </Toolbar>
                <DataTable
                    bare
                    dense
                    columns={columns}
                    rows={rows}
                    rowKey={(r) => r.introducer}
                    rowTone={(r) =>
                        r.status === 'ready'
                            ? undefined
                            : r.status === 'disabled'
                              ? undefined
                              : r.status === 'ready_no_audit'
                                ? 'warn'
                                : 'bad'
                    }
                    loading={loading && !data}
                    emptyTitle={data ? 'No introducers match' : 'No results'}
                    emptyDescription={data ? 'Try a different filter or search.' : 'Run the probe to see readiness.'}
                />
            </Card>
        </div>
    )
}
